import { GridRowParams } from "@mui/x-data-grid-premium";

import { isBillOfLadingRow, isManifestRow, ShippingPaperworkRow } from "./shippingPaperworkColumns";

/**
 * Where a `ManifestsV3` row click should go, in the two kinds the mixed grid
 * can produce. Both kinds open in place over the grid: a manifest opens the
 * same drawer V2's Manifests page opens, keyed by the `processedManifestId`
 * URL path segment; a bill of lading opens its own drawer, keyed by the
 * `?shippingDocumentId=` URL param (see `shippingDocumentDrawerParam.ts`).
 *
 * Reads the id off the row's populated side (`row.manifest.id` /
 * `row.billOfLading.id`), never the row's own `id`: an item's id is
 * `<kind>:<entity id>` so that the two source tables' independent uuid spaces
 * still produce a unique grid row identity, and that compound string is not a
 * valid entity id for either destination.
 *
 * Answers null for a row with neither side populated. The API fills exactly
 * one side on every row, so that case does not arise in practice, and there
 * is nowhere to navigate if it ever does.
 */
export type ManifestsV3RowDestination =
  | { kind: "manifest"; manifestId: string }
  | { kind: "billOfLading"; shippingDocumentId: string };

export function manifestsV3RowDestination(
  row: ShippingPaperworkRow
): ManifestsV3RowDestination | null {
  if (isBillOfLadingRow(row)) {
    return { kind: "billOfLading", shippingDocumentId: row.billOfLading.id };
  }
  if (isManifestRow(row)) {
    return { kind: "manifest", manifestId: row.manifest.id };
  }
  return null;
}

interface ManifestsV3RowClickArgs {
  openManifest: (manifestId: string) => void;
  openBillOfLading: (shippingDocumentId: string) => void;
}

/**
 * `ManifestsV3`'s actual `onRowClick` wiring, pulled out here so a test can
 * drive it with a `GridRowParams`-shaped object — the grid's own `id` field
 * alongside `row` — and confirm the composite row id never reaches
 * `manifestsV3RowDestination` in place of the entity id. Reads `params.row`
 * exclusively; `params.id` (the `<kind>:<entity id>` grid row id) is
 * intentionally never touched.
 */
export function manifestsV3RowClickHandler(
  params: Pick<GridRowParams<ShippingPaperworkRow>, "id" | "row">,
  { openManifest, openBillOfLading }: ManifestsV3RowClickArgs
): void {
  const destination = manifestsV3RowDestination(params.row);
  if (!destination) {
    return;
  }
  if (destination.kind === "billOfLading") {
    openBillOfLading(destination.shippingDocumentId);
    return;
  }
  openManifest(destination.manifestId);
}
